import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import { FormControl, InputLabel, MenuItem, Button as MuiButton, Select, TextField, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Dialog, Title, theme, useLoader } from 'thermia-react-container';
import GuideList from '../components/guideList';
import useTroubleshootService from '../service/useTroubleshootService';
import { GuideType, Item, TroubleshootTag, TroubleshootTagData } from '../types/types';

const SCROLL_DIFF_BEFORE_END_TO_LOAD_MORE_DATA = 1900;
const AMMOUNT_OF_GUIDES_TO_FETCH = 40;

type Props = {
  title: string;
  type: GuideType;
};

const TroubleshootSearch: FC<Props> = ({ title, type }) => {
  const { t, i18n } = useTranslation('troubleshoot');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Item[]>();
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [selectedTags, setSelectedTags] = useState<{ [id: number]: number }>({});
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isReady, setIsReady] = useState(false);
  const [tagData, setTagData] = useState<TroubleshootTagData>([]);
  const totalCount = useRef<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const { Loader } = useLoader();
  const { fetchTagdata, searchGuides } = useTroubleshootService();
  const navigate = useNavigate();

  const pushSelectedTagsToUrl = useCallback((selectedTagsValue: any) => {
    const url = new URL(window.location.href);
    url.searchParams.forEach((value, key) => {
      if (key.startsWith('tag')) {
        url.searchParams.delete(key);
      }
    });
    Object.keys(selectedTagsValue).forEach((key: any) => {
      url.searchParams.set('tag' + key, selectedTagsValue[key].toString());
    });
    window.history.pushState({}, '', url.toString());
  }, []);

  const submitSearch = useCallback(
    async (term: string, tags: { [id: number]: number }) => {
      setLoading(true);
      updateSearchUrl(term);
      pushSelectedTagsToUrl(tags);
      setHasSearched(false);
      totalCount.current = 0;
      const tagsIds = Object.values(tags);
      const fetchAnswer = await searchGuides(tagsIds, 0, term, AMMOUNT_OF_GUIDES_TO_FETCH, type);
      setSearchResults(fetchAnswer.items);
      totalCount.current = fetchAnswer.totalCount;
      setHasSearched(true);
      setLoading(false);
    },
    [pushSelectedTagsToUrl, searchGuides, type]
  );

  useEffect(() => {
    if (!isReady) {
      async function fetchInitialData() {
        try {
          setLoading(true);
          const fetchedData = await fetchTagdata(type);
          if (fetchedData) {
            setTagData(fetchedData);
            setIsReady(true);
            setLoading(false);
          }
        } catch (error) {
          console.log(error);
          setIsReady(true);
        }
        const tags = await setSelectedTagsFromUrlToState();
        const hasTags = Object.keys(tags).length > 0;
        const url = new URL(window.location.href);
        const searchKey: string = url.searchParams.get('searchterm') || '';
        setSearchTerm(searchKey);
        setSelectedTags(tags);

        if (hasTags || searchKey) {
          submitSearch(searchKey, tags);
        }
      }
      fetchInitialData();
    }
  }, [fetchTagdata, isReady, loading, navigate, selectedTags, submitSearch, t, tagData, type]);

  useEffect(() => {
    async function loadMoreData() {
      if (hasSearched && searchResults!.length < totalCount.current) {
        const tagsIds = Object.values(selectedTags);
        const skip = totalCount.current > 0 ? searchResults?.length : 0;
        const newData = await searchGuides(tagsIds, skip!, searchTerm, AMMOUNT_OF_GUIDES_TO_FETCH, type);
        totalCount.current = newData.totalCount;
        const newArray = [...searchResults!].concat(newData.items);
        setSearchResults(newArray);
      }
    }
    const handleScroll = async () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight,
        document.body.clientHeight,
        document.documentElement.clientHeight
      );
      const currentScroll = scrollTop + window.innerHeight;
      if (currentScroll + SCROLL_DIFF_BEFORE_END_TO_LOAD_MORE_DATA > docHeight && hasSearched && !loading) {
        setLoading(true);
        await loadMoreData();
        setLoading(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasSearched, loading, searchGuides, searchResults, searchTerm, selectedTags, type]);

  function resetAllFields() {
    setSearchTerm('');
    setSearchResults([]);
    setHasSearched(false);
    setSelectedTags({});
  }
  const updateLanguage = useCallback(async () => {
    setLoading(true);
    const fetchedData = await fetchTagdata(type);
    if (fetchedData) {
      setTagData(fetchedData);
      setIsReady(true);
    }
    setLoading(false);
  }, [fetchTagdata, type]);

  useEffect(() => {
    i18n.on('languageChanged', updateLanguage);
    return () => {
      i18n.off('languageChanged', updateLanguage);
    };
  }, [i18n, updateLanguage]);

  function defaultButtonText(category: string): string {
    return `${t('troubleshoot.selections.defaultButtonText')} ${category.toLowerCase()}`;
  }

  function makeEmptyTag(category: string): TroubleshootTag {
    return {
      id: 0,
      name: `\u00A0` //non-breaking space
    };
  }

  function updateSearchUrl(value: string) {
    const url = new URL(window.location.href);
    if (value !== '') {
      url.searchParams.set('searchterm', value ?? '');
      window.history.pushState({}, '', url.toString());
    } else {
      url.searchParams.delete('searchterm');
      window.history.pushState({}, '', url.toString());
    }
  }

  async function setSelectedTagsFromUrlToState() {
    const url = new URL(window.location.href);
    const newSelectedTags: { [id: number]: number } = {};
    url.searchParams.forEach((value, key) => {
      if (key.startsWith('tag')) {
        newSelectedTags[parseInt(key.slice(3))] = parseInt(value);
      }
    });
    return newSelectedTags;
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        submitSearch(searchTerm, selectedTags);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchTerm, selectedTags, submitSearch]);

  return (
    <>
      {isReady && (
        <>
          <Title text={title} />
          <div
            style={{
              position: 'relative',
              flexWrap: 'nowrap',
              backgroundColor: theme.palette.white.main,
              padding: '30px 20px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minWidth: 'fit-content'
            }}>
            <div
              style={{
                width: '100%',
                flexWrap: 'wrap',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '20px'
              }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <InfoIcon
                  sx={{
                    backgroundColor: 'white',
                    marginRight: '7px',
                    fontSize: '40px',
                    color: theme.palette.primary.light,
                    borderRadius: '7000px',
                    '&:hover': {
                      color: theme.palette.primary.main,
                      backgroundColor: 'black'
                    }
                  }}
                  onClick={() => setShowModal(true)}
                />
                <TextField
                  sx={{ backgroundColor: theme.palette.trueWhite.main, width: '320px', borderRadius: '7px' }}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      submitSearch(searchTerm, selectedTags);
                    }
                  }}
                  placeholder={t('troubleshoot.general.searchFreeText')}></TextField>
              </div>
              <div style={{ display: 'flex', maxWidth: '674px', flexWrap: 'wrap' }}>
                {tagData &&
                  tagData.map((tagCategory, index) => (
                    <div key={tagCategory.id} style={{ minWidth: 0 }}>
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel sx={{ backgroundColor: theme.palette.background.paper, borderBottom: 'none' }} key={tagCategory.name + index}>
                          {defaultButtonText(tagCategory.name)}
                        </InputLabel>
                        <Select
                          value={selectedTags[tagCategory.id] ?? 0}
                          key={tagCategory.name}
                          MenuProps={{
                            disableScrollLock: true
                          }}
                          style={{
                            minWidth: '190px',
                            borderRadius: 7,
                            backgroundColor: theme.palette.background.paper,
                            border: '1px solid #ced4da',
                            fontSize: 16,
                            padding: '3px',
                            margin: '9px 3px'
                          }}
                          onChange={(event) => {
                            const selectedItemId = event.target.value as number;
                            selectedTags[tagCategory.id] = selectedItemId;
                            if (selectedItemId === 0) {
                              delete selectedTags[tagCategory.id];
                            }
                            setSelectedTags({ ...selectedTags }); // must be a new object to trigger a rerender
                          }}
                          label={defaultButtonText(tagCategory.name)}>
                          {[makeEmptyTag(tagCategory.name), ...tagCategory.tags].map((item) => (
                            <MenuItem key={item.name + 'menu'} value={item.id}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  ))}
              </div>
              <div>
                <Button
                  style={{ marginLeft: '7px', width: '150px', height: '50px', borderRadius: '7px' }}
                  variant="outlined"
                  color="primary"
                  title={t('troubleshoot.selections.reset')}
                  onClick={() => resetAllFields()}
                />

                <Button
                  style={{ marginLeft: '7px', width: '150px', height: '50px', borderRadius: '7px' }}
                  variant="contained"
                  color="primary"
                  title={t('troubleshoot.general.search')}
                  onClick={async () => {
                    await submitSearch(searchTerm, selectedTags);
                  }}
                  icon={{ icon: <SearchIcon />, position: 'left' }}
                />
              </div>
            </div>
          </div>
          <Dialog open={showModal} id="popup-dialog">
            <div style={{ margin: '40px', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
              <Typography fontSize={20} style={{ marginBottom: '10px' }}>
                {t('troubleshoot.general.modalInfoTitle')}
              </Typography>
              {type === GuideType.TROUBLESHOOT && <Typography>{t('troubleshoot.general.modalTextTroubleshoot')}</Typography>}
              {type === GuideType.INSTALLATIONS_AND_FUNCTIONS && <Typography>{t('troubleshoot.general.modalTextInstallations')}</Typography>}
              {type === GuideType.PROJECTPLANNING_AND_DIMENSIONING && <Typography>{t('troubleshoot.general.modalTextProjectplanning')}</Typography>}

              <MuiButton variant="contained" style={{ width: '100%', marginTop: '20px' }} onClick={() => setShowModal(false)}>
                {t('troubleshoot.general.ok')}{' '}
              </MuiButton>
            </div>
          </Dialog>
          {hasSearched && (
            <div style={{ display: 'flex', padding: '12px', justifyContent: 'center', alignContent: 'center' }}>
              <Typography fontSize={18}>
                {t('troubleshoot.general.totalResults')} {totalCount.current ?? 0}
              </Typography>
            </div>
          )}
          {hasSearched && <GuideList type={type} large tagData={tagData} results={searchResults!} loading={loading} />}
        </>
      )}
      {loading && <Loader />}
    </>
  );
};

export default TroubleshootSearch;
